import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../utils/cn';
import { CloudinaryImage } from '@art-of-hospitality-solutions-llc/ui-kit/components/cloudinary-image';
import type { Asset } from '@Types/assets';

export type CloudImage = {
  id?: string;
  publicId: string;
  alt?: string;
  width?: number;
  height?: number;
};

type Columns = {
  base?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
};

type MediaGalleryProps = {
  images: (CloudImage | Asset)[];
  className?: string;
  thumbnailClassName?: string;
  columns?: Columns; // responsive column counts
  gapClassName?: string; // e.g., "gap-2"
  /** aspect ratio for thumbnails, e.g., 'square', 'video', '16/9', '4/3' */
  thumbAspect?: 'auto' | 'square' | 'video' | string;
  /** Custom empty state to render when there are no images */
  emptyState?: React.ReactNode;
};

// Internal normalized type the gallery/lightbox will actually use
type GalleryImage = {
  id: string;
  publicId: string;
  alt?: string;
  width?: number;
  height?: number;
};

// Type guard: is this a CloudImage?
function isCloudImage(img: CloudImage | Asset): img is CloudImage {
  return 'publicId' in img;
}

export function MediaGallery({
  images,
  className,
  thumbnailClassName,
  columns = { base: 2, sm: 3, md: 4 },
  gapClassName = 'gap-2',
  thumbAspect = 'square',
  emptyState,
}: MediaGalleryProps) {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState<number>(0);

  // Normalize incoming images to a single shape
  const normalizedImages: GalleryImage[] = useMemo(
    () =>
      images.map((img, i) => {
        if (isCloudImage(img)) {
          return {
            id: img.id ?? `cloud-${i}`,
            publicId: img.publicId,
            alt: img.alt,
            width: img.width,
            height: img.height,
          };
        }

        // Asset → map to cloudinary public id
        return {
          id: img.id ?? `asset-${i}`,
          publicId: img.cloudinaryId, // or img.cloudinaryAssetId if that’s the one you use
          alt: undefined, // no alt on Asset (you can change this later if you add one)
        };
      }),
    [images],
  );

  const count = normalizedImages.length;

  const gridColsClass = useMemo(() => {
    const base = columns.base ?? 2;
    return cn(
      `grid grid-cols-${Math.min(Math.max(base, 1), 6)}`,
      columns.sm && `sm:grid-cols-${Math.min(Math.max(columns.sm, 1), 8)}`,
      columns.md && `md:grid-cols-${Math.min(Math.max(columns.md, 1), 12)}`,
      columns.lg && `lg:grid-cols-${Math.min(Math.max(columns.lg, 1), 12)}`,
      columns.xl && `xl:grid-cols-${Math.min(Math.max(columns.xl, 1), 12)}`,
    );
  }, [columns]);

  const openAt = (i: number) => {
    setIndex(i);
    setOpen(true);
  };

  const goPrev = useCallback(() => {
    setIndex((i) => (i - 1 + count) % count);
  }, [count]);

  const goNext = useCallback(() => {
    setIndex((i) => (i + 1) % count);
  }, [count]);

  // Show empty state if no images
  if (count === 0) {
    return (
      <div className={cn('w-full', className)}>
        {emptyState !== undefined ? (
          emptyState
        ) : (
          <div className="flex flex-col items-center justify-center rounded border border-dashed border-zinc-300 bg-zinc-50 py-12 text-center">
            <div className="text-sm font-medium text-zinc-700">
              No photos available
            </div>
            <div className="mt-1 text-xs text-zinc-500">
              Images will appear here when added
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={cn('w-full', className)}>
      <div className={cn(gridColsClass, gapClassName)}>
        {normalizedImages.map((img, i) => (
          <button
            key={img.id ?? img.publicId ?? i}
            type="button"
            className={cn(
              'group relative overflow-hidden rounded-md focus:ring-2 focus:ring-white/40 focus:outline-none',
              // aspect helpers
              thumbAspect === 'square' && 'aspect-square',
              thumbAspect === 'video' && 'aspect-video',
            )}
            onClick={() => openAt(i)}
            aria-label={`Open image ${i + 1} of ${count}`}
          >
            <CloudinaryImage
              publicId={img.publicId}
              width={800} // thumbnail transform
              mode="fill" // fill/crop for grid thumbs
              alt={img.alt ?? ''}
              className={cn(
                'h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]',
                thumbnailClassName,
              )}
            />
          </button>
        ))}
      </div>

      {open && count > 0 && (
        <Lightbox
          images={normalizedImages}
          index={index}
          onClose={() => setOpen(false)}
          onPrev={goPrev}
          onNext={goNext}
          setIndex={setIndex}
        />
      )}
    </div>
  );
}

// ————————————————————————————————
// Lightbox (Modal) — no external deps
// ————————————————————————————————
type LightboxProps = {
  images: GalleryImage[];
  index: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  setIndex: (n: number) => void;
};

function Lightbox({
  images,
  index,
  onClose,
  onPrev,
  onNext,
  setIndex,
}: LightboxProps) {
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const startX = useRef<number | null>(null);
  const startY = useRef<number | null>(null);

  // lock scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // keyboard navigation
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onPrev, onNext]);

  // click backdrop to close
  const onBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only close if clicking directly on the backdrop, not on child elements
    if (
      e.target === overlayRef.current ||
      e.currentTarget === overlayRef.current
    ) {
      onClose();
    }
  };

  // basic touch swipe
  const onTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    const t = e.touches[0];
    startX.current = t.clientX;
    startY.current = t.clientY;
  };
  const onTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    if (startX.current == null || startY.current == null) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - startX.current;
    const dy = t.clientY - startY.current;
    startX.current = null;
    startY.current = null;

    // horizontal swipe dominates
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) {
      if (dx > 0) onPrev();
      else onNext();
    }
  };

  const img = images[index];

  return (
    <div
      ref={overlayRef}
      onMouseDown={onBackdrop}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      className="fixed inset-0 z-100 flex items-center justify-center bg-black/80 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
    >
      {/* Close */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        onMouseDown={(e) => e.stopPropagation()}
        aria-label="Close"
        className="absolute top-4 right-4 z-101 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 focus:ring-2 focus:ring-white/40 focus:outline-none"
      >
        <X className="h-5 w-5" />
      </button>

      {/* Prev */}
      {images.length > 1 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onPrev();
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          aria-label="Previous image"
          className="absolute top-1/2 left-2 z-101 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white hover:bg-white/20 focus:ring-2 focus:ring-white/40 focus:outline-none"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
      )}

      {/* Next */}
      {images.length > 1 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onNext();
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          aria-label="Next image"
          className="absolute top-1/2 right-2 z-101 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white hover:bg-white/20 focus:ring-2 focus:ring-white/40 focus:outline-none"
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      )}

      {/* Image */}
      <figure
        className="relative z-101 mx-4 flex max-h-[90vh] max-w-[92vw] flex-col items-center justify-center"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <CloudinaryImage
          publicId={img.publicId}
          width={1600}
          mode="scale"
          alt={img.alt ?? ''}
          className="max-h-[80vh] w-auto max-w-full object-contain select-none"
        />
        {(img.alt || images.length > 1) && (
          <figcaption className="mt-3 text-center text-sm text-white/80">
            {img.alt && <span className="block">{img.alt}</span>}
            {images.length > 1 && (
              <span className="mt-1 block">
                {index + 1} / {images.length}
              </span>
            )}
          </figcaption>
        )}
      </figure>

      {/* Dots */}
      {images.length > 1 && (
        <div
          className="absolute bottom-6 z-101 flex items-center gap-2"
          onMouseDown={(e) => e.stopPropagation()}
        >
          {images.map((_, i) => {
            const active = i === index;
            return (
              <button
                key={i}
                aria-label={`Go to image ${i + 1}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setIndex(i);
                }}
                onMouseDown={(e) => e.stopPropagation()}
                className={cn(
                  'h-2 rounded-full bg-white/50 transition-all',
                  active ? 'w-6 bg-white' : 'w-2',
                )}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
